import { useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Building2, Bot, Banknote, CreditCard, TrendingUp, ShieldCheck, ArrowRight, Check, ExternalLink, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PublicNav, PublicFooter, Seo, PLANS as PLAN_DATA } from "@/components/public/PublicChrome";
import { trackAcq, captureTouch } from "@/lib/acquisition";
import { COVER_SRC, openAmazon, bmTrack, BM_EVENTS } from "@/lib/businessMastery";
import { TOOLS } from "@/pages/tools/toolsConfig";

const HERO_BG = "https://images.unsplash.com/photo-1741356474357-72188f76e801?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGJsYWNrJTIwYW5kJTIwZ29sZCUyMGx1eHVyeSUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzgzMzgwMDU0fDA&ixlib=rb-4.1.0&q=85";
const OFFICE = "https://images.unsplash.com/photo-1764410481612-7544525b2991?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBvZmZpY2UlMjBlbnRyZXByZW5ldXIlMjBidXNpbmVzc21hbnxlbnwwfHx8fDE3ODMzODAwNTR8MA&ixlib=rb-4.1.0&q=85";

const FEATURES = [
  { icon: Building2, title: "Business Formation", desc: "Launch your LLC or corporation with a guided, state-by-state checklist." },
  { icon: CreditCard, title: "Business Credit", desc: "Build and monitor business credit that's separate from your personal score." },
  { icon: Banknote, title: "Funding Center", desc: "Discover loans, grants, and investors matched to your business stage." },
  { icon: Bot, title: "AI Advisor", desc: "An expert business consultant on call 24/7, powered by advanced AI." },
  { icon: TrendingUp, title: "Progress Tracker", desc: "See exactly where you are and what to do next across every module." },
  { icon: ShieldCheck, title: "Enterprise Security", desc: "Bank-grade protection for your documents, contracts, and data." },
];

const PLANS = PLAN_DATA;

const HERO_ANIM = { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.6 } };
const CARD_VIEW = { initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true } };

export default function Landing() {
  const navigate = useNavigate();
  useEffect(() => {
    captureTouch();
    const ref = new URLSearchParams(window.location.search).get("ref");
    if (ref) try { localStorage.setItem("fv_ref", ref); } catch (e) { /* ignore */ }
    trackAcq("hero_free_tools_impression");
    trackAcq("homepage_tool_card_impression");
  }, []);
  return (
    <div className="min-h-screen bg-[#0A0A0B] text-white overflow-x-hidden">
      <Seo title="Start and Organize Your Business" path="/"
        description="Overwhelmed starting a business? Fairview Systems guides you from idea to open — validate, name, form, plan, and set up business banking, all in one place." />
      <PublicNav />

      {/* Hero */}
      <section className="relative pt-40 pb-28 px-6 fv-grain">
        <div className="absolute inset-0 z-0">
          <img src={HERO_BG} alt="Abstract black and gold background" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0B]/60 via-[#0A0A0B]/80 to-[#0A0A0B]" />
        </div>
        <div className="relative z-10 max-w-5xl mx-auto text-center">
          <motion.div {...HERO_ANIM}>
            <div className="inline-block mb-6 px-4 py-1.5 rounded-full border border-[#D4AF37]/30 text-[#D4AF37] text-xs tracking-[0.2em] uppercase">
              From idea to operating business
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-7xl tracking-tight leading-none mb-6">
              Start and Organize Your Business <br className="hidden sm:block" /> — All in{" "}
              <span className="fv-gold-text">One Place</span>
            </h1>
            <p className="text-base sm:text-lg text-[#A1A1AA] max-w-2xl mx-auto mb-9 leading-relaxed">
              Overwhelmed by where to begin? Fairview Systems guides you step by step — validate your
              idea, name and form your business, choose a legal structure, write your plan, set up
              business banking, and organize everything, with an AI advisor at your side.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button size="lg" data-testid="hero-free-tools-btn"
                onClick={() => { trackAcq("hero_free_tools_click"); navigate("/tools"); }}
                className="fv-gold-gradient text-[#0A0A0B] hover:opacity-90 font-semibold rounded-full px-8 h-12 text-base">
                Try a Free Business Tool <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button size="lg" variant="outline" onClick={() => navigate("/register?plan=growth")} data-testid="hero-getstarted-btn"
                className="border-[#D4AF37]/40 bg-transparent text-white hover:bg-white/5 rounded-full px-8 h-12 text-base">
                Start your free 7-day trial
              </Button>
              <Button size="lg" variant="ghost" onClick={() => navigate("/demo")} data-testid="hero-demo-btn"
                className="text-[#A1A1AA] hover:text-white hover:bg-white/5 rounded-full px-6 h-12 text-base">
                Explore Interactive Demo
              </Button>
            </div>
            <p className="text-xs text-[#71717A] mt-4">Free tools — no signup required · No credit card to start your trial</p>
            <div className="mt-5 flex justify-center">
              <button data-testid="hero-watch-video-btn" onClick={() => navigate("/demo/video")}
                className="inline-flex items-center gap-2.5 text-sm text-[#D4AF37] hover:text-[#E6C458] transition-colors">
                <span className="w-8 h-8 rounded-full border border-[#D4AF37]/40 flex items-center justify-center"><Play className="w-3.5 h-3.5 fill-current" /></span>
                Watch 30-Second Demo Video
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Everything you need — capability strip */}
      <section className="relative py-20 px-6 border-t border-white/5" data-testid="home-capabilities">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <div className="text-[#D4AF37] text-xs tracking-[0.2em] uppercase mb-3">Everything you need to begin</div>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight">One platform, every step of starting a business</h2>
            <p className="text-[#A1A1AA] text-sm mt-3 max-w-2xl mx-auto">Stop stitching together a dozen services. Fairview Systems walks first-time founders through each step in the right order.</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {[
              ["Validate your idea", "Test whether your idea holds up"],
              ["Generate a name", "Find an available, on-brand name"],
              ["Form your business", "Step-by-step formation guidance"],
              ["Choose a structure", "LLC vs. S-Corp made simple"],
              ["Write a business plan", "A lender-ready plan, guided"],
              ["Set up business banking", "Guided setup + banking resolution doc"],
              ["Business documents", "Contracts and key paperwork"],
              ["AI business advisor", "Answers whenever you're stuck"],
              ["Organize & workflows", "Keep every task in one place"],
              ["Virtual address", "Guidance on a business address"],
              ["Business phone & website", "Guidance to get set up online"],
              ["Prepare to scale", "Systems that grow with you"],
            ].map(([t, d]) => (
              <div key={t} data-testid={`cap-${t.toLowerCase().replace(/[^a-z]+/g, '-')}`}
                className="rounded-xl border border-white/8 bg-[#121214] p-4">
                <div className="text-white text-sm font-medium mb-1">{t}</div>
                <div className="text-[#71717A] text-xs leading-relaxed">{d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why one platform */}
      <section className="relative py-20 px-6 border-t border-white/5" data-testid="home-why-one-platform">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tight mb-3">Stop Piecing Your Business Together</h2>
          <p className="text-[#A1A1AA] text-sm mb-10 max-w-2xl mx-auto">New founders lose weeks juggling separate tools for naming, formation, plans, and paperwork. Fairview keeps it all connected so nothing falls through the cracks.</p>
          <div className="grid sm:grid-cols-3 gap-6 text-left">
            {[
              ["Clear next step, always", "No guessing what to do next — you're guided from idea to operating business."],
              ["Your work stays connected", "Your idea, name, plan, and documents live together, not scattered across apps."],
              ["An advisor in your corner", "Ask the AI advisor anything, anytime, instead of hunting through forums."],
            ].map(([t, d]) => (
              <div key={t} className="rounded-xl border border-white/8 bg-[#121214] p-5">
                <div className="text-[#D4AF37] font-heading text-lg mb-2">{t}</div>
                <p className="text-[#A1A1AA] text-sm leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative py-20 px-6 border-t border-white/5" data-testid="home-tools-section">
        <div className="max-w-7xl mx-auto">
          <div className="mb-10 flex items-end justify-between flex-wrap gap-4">
            <div>
              <div className="text-[#D4AF37] text-xs tracking-[0.2em] uppercase mb-3">Free · no signup to start</div>
              <h2 className="font-heading text-3xl sm:text-4xl tracking-tight">Start with a free business tool</h2>
              <p className="text-[#A1A1AA] text-sm mt-2 max-w-xl">Get a useful result in seconds, then save it to your free account and keep building inside Fairview Systems.</p>
              <Link to="/learn/how-to-start-a-business" data-testid="home-guide-link"
                onClick={() => trackAcq("homepage_guide_link_click")}
                className="inline-flex items-center gap-1 text-[#D4AF37] text-sm mt-3 hover:underline">
                New to this? Follow our free step-by-step Start a Business Checklist <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
            <Link to="/tools" data-testid="home-tools-viewall" onClick={() => trackAcq("homepage_tool_card_click", { tool: "all" })}
              className="text-[#D4AF37] text-sm hover:underline whitespace-nowrap">View all free tools →</Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {Object.values(TOOLS).map((t, i) => (
              <motion.div key={t.slug} {...CARD_VIEW} transition={{ delay: i * 0.05 }}>
                <Link to={`/tools/${t.slug}`} data-testid={`home-tool-card-${t.slug}`}
                  onClick={() => trackAcq("homepage_tool_card_click", { tool: t.slug })}
                  className="block h-full fv-card-hover rounded-2xl border border-white/8 bg-[#121214] p-6 hover:border-[#D4AF37]/40 transition-colors">
                  <div className="text-[10px] tracking-[0.18em] uppercase text-[#D4AF37] mb-3">{t.eyebrow}</div>
                  <h3 className="font-heading text-xl mb-2 leading-snug">{t.cardTitle}</h3>
                  <p className="text-[#A1A1AA] text-sm leading-relaxed line-clamp-3">{t.whatItDoes || t.intro}</p>
                  <span className="inline-flex items-center gap-1 text-[#D4AF37] text-sm mt-4">Try it free <ArrowRight className="w-3.5 h-3.5" /></span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Business Resource — Business Mastery */}
      <section className="relative py-20 px-6 border-t border-white/5" data-testid="home-business-mastery">
        <div className="max-w-6xl mx-auto">
          <div className="grid gap-8 lg:grid-cols-[300px_1fr] lg:gap-14 lg:items-center">
            {/* Header: label + title + subheading (mobile: 1-2, desktop: right/top) */}
            <div className="lg:col-start-2 lg:row-start-1 lg:self-end">
              <div className="text-[#D4AF37] text-xs tracking-[0.2em] uppercase mb-3">Featured Business Resource</div>
              <h2 className="font-heading text-3xl sm:text-4xl tracking-tight">Business Mastery</h2>
              <p className="text-[#D4AF37]/90 text-sm mt-2 tracking-wide">Ownership • Systems • Scale • Legacy</p>
              <p className="text-[#A1A1AA] text-sm mt-1">By Calvin Langford</p>
            </div>

            {/* Cover (mobile: 3, desktop: left, full height) */}
            <div className="flex justify-center lg:col-start-1 lg:row-start-1 lg:row-span-2 lg:self-center">
              <img src={COVER_SRC} alt="Business Mastery by Calvin Langford book cover" data-testid="home-bm-cover"
                className="w-52 sm:w-60 lg:w-full lg:max-w-[300px] h-auto object-contain rounded-xl border border-[#D4AF37]/30 bg-[#0F0F10]" />
            </div>

            {/* Body: description + CTAs (mobile: 4-6, desktop: right/bottom) */}
            <div className="lg:col-start-2 lg:row-start-2 lg:self-start">
              <p className="text-[#A1A1AA] leading-relaxed mb-4">
                Building a business takes more than a great idea. Business Mastery explores the foundations of
                ownership, systems, growth, scaling, and long-term business development — with practical exercises
                designed to help entrepreneurs apply what they learn.
              </p>
              <p className="text-[#D4D4D8] leading-relaxed mb-6">
                Continue your business education with Business Mastery, featured in the Fairview Systems Education &amp; Training experience.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button data-testid="home-bm-buy"
                  onClick={() => openAmazon({ source: "homepage", source_page: "homepage", location: "homepage_featured_resource", cta_location: "homepage_featured_resource" }, { public: true })}
                  className="fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full h-12 px-7 hover:opacity-90">
                  Buy on Amazon <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
                <Button variant="outline" data-testid="home-bm-learnmore"
                  onClick={() => { bmTrack(BM_EVENTS.referralClick, { source: "homepage", source_page: "homepage", location: "homepage_featured_resource", cta_location: "homepage_featured_resource" }, { public: true }); navigate("/business-mastery"); }}
                  className="border-[#D4AF37]/40 bg-transparent text-white hover:bg-white/5 rounded-full h-12 px-7">
                  Learn More
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="relative py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="mb-14">
            <div className="text-[#D4AF37] text-xs tracking-[0.2em] uppercase mb-3">Everything you need</div>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight max-w-2xl">
              A complete toolkit for the modern founder
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((f, i) => (
              <motion.div key={f.title} {...CARD_VIEW} transition={{ delay: i * 0.06 }}
                className="fv-card-hover rounded-2xl border border-white/8 bg-[#121214] p-7">
                <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/20 flex items-center justify-center mb-5">
                  <f.icon className="w-6 h-6 text-[#D4AF37]" />
                </div>
                <h3 className="font-heading text-2xl mb-2">{f.title}</h3>
                <p className="text-[#A1A1AA] text-sm leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Showcase */}
      <section className="py-16 px-6">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-[#D4AF37] text-xs tracking-[0.2em] uppercase mb-3">Built to scale</div>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight mb-5">
              One secure command center for your entire business
            </h2>
            <p className="text-[#A1A1AA] leading-relaxed mb-6">
              From day-one formation to enterprise operations, Fairview grows with you. New modules
              plug in seamlessly so your business operating system is never out of date.
            </p>
            <ul className="space-y-3">
              {["Guided step-by-step workflows", "AI advisor trained on business strategy", "Bank-grade document security"].map((t) => (
                <li key={t} className="flex items-center gap-3 text-[#E4E4E7]">
                  <span className="w-5 h-5 rounded-full fv-gold-gradient flex items-center justify-center shrink-0">
                    <Check className="w-3 h-3 text-[#0A0A0B]" />
                  </span>{t}
                </li>
              ))}
            </ul>
          </div>
          <div className="relative rounded-2xl overflow-hidden border border-white/8">
            <img src={OFFICE} alt="Luxury office" className="w-full h-full object-cover max-h-[520px]" />
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <div className="text-[#D4AF37] text-xs tracking-[0.2em] uppercase mb-3">Pricing</div>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight">Choose your plan</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {PLANS.map((p) => (
              <div key={p.name}
                className={`relative rounded-2xl p-8 border ${p.featured ? "border-[#D4AF37]/40 bg-[#151310]" : "border-white/8 bg-[#121214]"}`}>
                {p.featured && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full fv-gold-gradient text-[#0A0A0B] text-[10px] font-bold tracking-wide uppercase">
                    Recommended
                  </div>
                )}
                <h3 className="font-heading text-2xl mb-1">{p.name}</h3>
                <p className="text-[#71717A] text-sm mb-5">{p.tag}</p>
                <div className="mb-6"><span className="font-heading text-5xl fv-gold-text">${p.price}</span><span className="text-[#71717A]">/mo</span></div>
                <ul className="space-y-3 mb-8">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm text-[#D4D4D8]">
                      <Check className="w-4 h-4 text-[#D4AF37] mt-0.5 shrink-0" />{f}
                    </li>
                  ))}
                </ul>
                <Button onClick={() => { trackAcq("plan_selected", { plan_id: p.name.toLowerCase(), billing_cycle: "monthly" }); navigate(`/register?plan=${p.name.toLowerCase()}`); }} data-testid={`pricing-trial-${p.name.toLowerCase()}`}
                  className={`w-full rounded-full h-11 ${p.featured ? "fv-gold-gradient text-[#0A0A0B] font-semibold hover:opacity-90" : "bg-white/5 border border-white/15 text-white hover:bg-white/10"}`}>
                  Start Your Free 7-Day Trial
                </Button>
                <p className="text-center text-[11px] text-[#71717A] mt-3">No credit card required</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Button variant="outline" onClick={() => navigate("/demo")} data-testid="pricing-demo-btn"
              className="border-[#D4AF37]/40 bg-transparent text-white hover:bg-white/5 rounded-full px-8 h-12">
              Explore Interactive Demo <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
